import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { scansApi, patientsApi, generationApi, getFileUrl } from '../../lib/api';
import { getToken } from '../../lib/auth';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Upload,
  Image,
  CheckCircle,
  Loader2,
  Baby,
  Sparkles,
  Ruler,
  RefreshCw,
  Download,
  Eye,
  CloudUpload
} from 'lucide-react';

// Varsayılan ölçü değerleri
const DEFAULT_MEASUREMENTS = {
  fromen: 30, burun: 8, goztepe: 20, bioc_cap: 45, cene: 25,
  agiz_capi: 15, onarka_bas: 90, bpd: 50, hc: 180, goz: 12, ust_dudak: 8,
  gender: 'female'
};

// Ölçü alanları
const MEASUREMENT_FIELDS = [
  { key: 'bpd', label: 'BPD', unit: 'mm', min: 20, max: 100 },
  { key: 'hc', label: 'HC', unit: 'mm', min: 100, max: 400 },
  { key: 'fromen', label: 'Fromen', unit: 'mm', min: 15, max: 50 },
  { key: 'burun', label: 'Burun', unit: 'mm', min: 3, max: 20 },
  { key: 'goztepe', label: 'Göztepe', unit: 'mm', min: 10, max: 40 },
  { key: 'bioc_cap', label: 'BiocÇap', unit: 'mm', min: 25, max: 70 },
  { key: 'cene', label: 'Çene', unit: 'mm', min: 10, max: 50 },
  { key: 'agiz_capi', label: 'Ağız', unit: 'mm', min: 5, max: 30 },
  { key: 'goz', label: 'Göz', unit: 'mm', min: 5, max: 25 },
  { key: 'ust_dudak', label: 'Üst Dudak', unit: 'mm', min: 3, max: 20 },
];

export const StudioPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const scanId = searchParams.get('scanId');
  const fileInputRef = useRef(null);

  // States
  const [scan, setScan] = useState(null);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  
  // Images
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [ultrasoundUrl, setUltrasoundUrl] = useState(null);
  const [fetalFaceUrl, setFetalFaceUrl] = useState(null);
  
  // Measurements
  const [measurements, setMeasurements] = useState({ ...DEFAULT_MEASUREMENTS });
  const [showMeasurements, setShowMeasurements] = useState(false);

  // Fetch image with auth
  const fetchAuthImage = useCallback(async (imageUrl, setter) => {
    if (!imageUrl) return;
    try {
      const token = getToken();
      const fullUrl = getFileUrl(imageUrl);
      const response = await fetch(fullUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        setter(URL.createObjectURL(blob));
      }
    } catch (error) {
      console.error('Failed to fetch image:', error);
    }
  }, []);

  // Fetch scan data
  const fetchScanData = useCallback(async () => {
    if (!scanId) {
      toast.error('Tarama ID bulunamadı');
      navigate('/doctor/patients');
      return;
    }

    try {
      setLoading(true);
      const scanData = await scansApi.get(scanId);
      setScan(scanData);

      // Fetch patient
      const patientData = await patientsApi.get(scanData.patient_id);
      setPatient(patientData);

      // Load measurements if exists
      if (scanData.metadata?.measurements) {
        setMeasurements(prev => ({ ...prev, ...scanData.metadata.measurements }));
      }

      // Load images
      if (scanData.ultrasound_image_url) {
        fetchAuthImage(scanData.ultrasound_image_url, setUltrasoundUrl);
      }
      if (scanData.baby_face_url) {
        fetchAuthImage(scanData.baby_face_url, setFetalFaceUrl);
        setShowMeasurements(true); // Show measurements panel if already generated
      }
    } catch (error) {
      toast.error('Veri yüklenirken hata oluştu');
      navigate('/doctor/patients');
    } finally {
      setLoading(false);
    }
  }, [scanId, navigate, fetchAuthImage]);

  useEffect(() => {
    fetchScanData();
  }, [fetchScanData]);

  // Cleanup blob URLs
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      if (ultrasoundUrl) URL.revokeObjectURL(ultrasoundUrl);
      if (fetalFaceUrl) URL.revokeObjectURL(fetalFaceUrl);
    };
  }, [previewUrl, ultrasoundUrl, fetalFaceUrl]);

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen bir görsel dosyası seçin');
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  // Upload ultrasound and auto-generate
  const handleUploadAndGenerate = async () => {
    if (!selectedFile) {
      toast.error('Lütfen bir ultrason görseli seçin');
      return;
    }

    setUploading(true);
    setGenerating(true);

    try {
      // Step 1: Upload ultrasound
      toast.info('Ultrason yükleniyor...');
      const uploadedScan = await scansApi.uploadUltrasound(scanId, selectedFile);
      setScan(uploadedScan);
      
      if (uploadedScan.ultrasound_image_url) {
        fetchAuthImage(uploadedScan.ultrasound_image_url, setUltrasoundUrl);
      }

      // Step 2: Auto-generate fetal face
      toast.info('Fetal yüz oluşturuluyor...');
      const result = await generationApi.generate(scanId, 'fetal');
      
      // Step 3: Refresh scan data
      const updatedScan = await scansApi.get(scanId);
      setScan(updatedScan);
      
      if (updatedScan.baby_face_url) {
        fetchAuthImage(updatedScan.baby_face_url, setFetalFaceUrl);
      }

      toast.success('Fetal yüz başarıyla oluşturuldu!');
      setShowMeasurements(true);
      setSelectedFile(null);
      setPreviewUrl(null);
    } catch (error) {
      toast.error(error.message || 'İşlem başarısız');
    } finally {
      setUploading(false);
      setGenerating(false);
    }
  };

  // Regenerate with measurements
  const handleRegenerate = async () => {
    setRegenerating(true);

    try {
      // Save measurements first
      await scansApi.update(scanId, {
        metadata: { measurements }
      });

      // Regenerate
      toast.info('Ölçülere göre yeniden oluşturuluyor...');
      const result = await generationApi.generate(scanId, 'fetal');
      
      // Refresh
      const updatedScan = await scansApi.get(scanId);
      setScan(updatedScan);
      
      if (updatedScan.baby_face_url) {
        // Clear old URL first
        if (fetalFaceUrl) URL.revokeObjectURL(fetalFaceUrl);
        setFetalFaceUrl(null);
        
        // Fetch new image
        setTimeout(() => {
          fetchAuthImage(updatedScan.baby_face_url, setFetalFaceUrl);
        }, 100);
      }

      toast.success('Görsel güncellendi!');
    } catch (error) {
      toast.error(error.message || 'Yeniden oluşturma başarısız');
    } finally {
      setRegenerating(false);
    }
  };

  // Handle measurement change
  const handleMeasurementChange = (key, value) => {
    setMeasurements(prev => ({ ...prev, [key]: parseFloat(value) || '' }));
  };

  if (loading) {
    return (
      <DashboardLayout title="Analiz Stüdyosu">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-[#5A67D8]" />
        </div>
      </DashboardLayout>
    );
  }

  if (!scan || !patient) {
    return (
      <DashboardLayout title="Analiz Stüdyosu">
        <div className="text-center py-20">
          <p className="text-slate-500">Tarama bulunamadı</p>
          <Button onClick={() => navigate('/doctor/patients')} className="mt-4">
            Hastalara Dön
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout 
      title="Analiz Stüdyosu" 
      subtitle={`${patient.first_name} ${patient.last_name}`}
    >
      <div className="space-y-6" data-testid="studio-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate(`/doctor/patients/${patient.id}`)}
            className="text-slate-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Hastaya Dön
          </Button>
          <Button variant="outline" onClick={fetchScanData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Yenile
          </Button>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* Left: Ultrasound Upload */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Image className="w-5 h-5 text-[#5A67D8]" />
                Ultrason Görseli
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Current Ultrasound or Upload Area */}
              {ultrasoundUrl ? (
                <div className="space-y-4">
                  <div className="relative rounded-xl overflow-hidden bg-slate-100 aspect-video">
                    <img
                      src={ultrasoundUrl}
                      alt="Ultrason"
                      className="w-full h-full object-contain"
                      data-testid="ultrasound-preview"
                    />
                    <div className="absolute top-2 right-2">
                      <span className="px-2 py-1 bg-green-500 text-white text-xs rounded-full flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Yüklendi
                      </span>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Farklı Görsel Yükle
                  </Button>
                </div>
              ) : (
                <div 
                  className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center cursor-pointer hover:border-[#5A67D8] hover:bg-[#F8F7FC] transition-all"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {previewUrl ? (
                    <div className="space-y-4">
                      <img
                        src={previewUrl}
                        alt="Preview"
                        className="max-h-48 mx-auto rounded-lg"
                      />
                      <p className="text-sm text-slate-600">{selectedFile?.name}</p>
                    </div>
                  ) : (
                    <>
                      <CloudUpload className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                      <p className="text-lg font-medium text-slate-600 mb-2">
                        Ultrason Görselini Yükleyin
                      </p>
                      <p className="text-sm text-slate-400">
                        Tıklayın veya sürükleyip bırakın
                      </p>
                      <p className="text-xs text-slate-400 mt-2">
                        PNG, JPG, JPEG (max 10MB)
                      </p>
                    </>
                  )}
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="ultrasound-input"
              />

              {/* Upload & Generate Button */}
              {selectedFile && !ultrasoundUrl && (
                <Button
                  className="w-full bg-[#5A67D8] hover:bg-[#4F46E5] h-12"
                  onClick={handleUploadAndGenerate}
                  disabled={uploading || generating}
                  data-testid="upload-generate-btn"
                >
                  {uploading || generating ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      {uploading ? 'Yükleniyor...' : 'Oluşturuluyor...'}
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Yükle ve Fetal Yüz Oluştur
                    </>
                  )}
                </Button>
              )}

              {/* Generate for existing ultrasound */}
              {ultrasoundUrl && !fetalFaceUrl && (
                <Button
                  className="w-full bg-[#5A67D8] hover:bg-[#4F46E5] h-12"
                  onClick={async () => {
                    setGenerating(true);
                    try {
                      await generationApi.generate(scanId, 'fetal');
                      const updatedScan = await scansApi.get(scanId);
                      setScan(updatedScan);
                      if (updatedScan.baby_face_url) {
                        fetchAuthImage(updatedScan.baby_face_url, setFetalFaceUrl);
                      }
                      toast.success('Fetal yüz oluşturuldu!');
                      setShowMeasurements(true);
                    } catch (error) {
                      toast.error(error.message);
                    } finally {
                      setGenerating(false);
                    }
                  }}
                  disabled={generating}
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Oluşturuluyor...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Fetal Yüz Oluştur
                    </>
                  )}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Right: Fetal Face Result */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Baby className="w-5 h-5 text-[#5A67D8]" />
                Fetal Yüz Sonucu
              </CardTitle>
            </CardHeader>
            <CardContent>
              {fetalFaceUrl ? (
                <div className="space-y-4">
                  <div className="relative rounded-xl overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100">
                    <img
                      src={fetalFaceUrl}
                      alt="Fetal Yüz"
                      className="w-full h-auto"
                      data-testid="fetal-face-result"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1" asChild>
                      <a href={fetalFaceUrl} download="fetal-yuz.png">
                        <Download className="w-4 h-4 mr-2" />
                        İndir
                      </a>
                    </Button>
                    <Button variant="outline" className="flex-1" asChild>
                      <a href={fetalFaceUrl} target="_blank" rel="noopener noreferrer">
                        <Eye className="w-4 h-4 mr-2" />
                        Tam Ekran
                      </a>
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-24 h-24 rounded-full bg-[#E0D7FF] flex items-center justify-center mb-4">
                    <Baby className="w-12 h-12 text-[#5A67D8]" />
                  </div>
                  <p className="text-slate-500 mb-2">Henüz fetal yüz oluşturulmadı</p>
                  <p className="text-sm text-slate-400">
                    Ultrason yükledikten sonra otomatik oluşturulacak
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Measurements Panel - Shows after generation */}
        {showMeasurements && fetalFaceUrl && (
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Ruler className="w-5 h-5 text-[#5A67D8]" />
                  Biyometrik Ölçümler (İnce Ayar)
                </CardTitle>
                <Button
                  onClick={handleRegenerate}
                  disabled={regenerating}
                  className="bg-[#5A67D8] hover:bg-[#4F46E5]"
                  data-testid="regenerate-btn"
                >
                  {regenerating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Güncelleniyor...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Ölçülere Göre Yenile
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Gender Selection */}
                <div className="flex gap-4 mb-4">
                  <label className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    measurements.gender === 'female' 
                      ? 'border-[#5A67D8] bg-[#F8F7FC]' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}>
                    <input
                      type="radio"
                      name="gender"
                      value="female"
                      checked={measurements.gender === 'female'}
                      onChange={(e) => handleMeasurementChange('gender', e.target.value)}
                      className="w-4 h-4"
                    />
                    <span className="text-pink-500">♀</span>
                    <span>Kız</span>
                  </label>
                  <label className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    measurements.gender === 'male' 
                      ? 'border-[#5A67D8] bg-[#F8F7FC]' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}>
                    <input
                      type="radio"
                      name="gender"
                      value="male"
                      checked={measurements.gender === 'male'}
                      onChange={(e) => handleMeasurementChange('gender', e.target.value)}
                      className="w-4 h-4"
                    />
                    <span className="text-blue-500">♂</span>
                    <span>Erkek</span>
                  </label>
                </div>

                {/* Measurements Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                  {MEASUREMENT_FIELDS.map((field) => (
                    <div key={field.key} className="space-y-1">
                      <Label className="text-xs text-slate-500">
                        {field.label} ({field.unit})
                      </Label>
                      <Input
                        type="number"
                        min={field.min}
                        max={field.max}
                        step="0.1"
                        value={measurements[field.key] || ''}
                        onChange={(e) => handleMeasurementChange(field.key, e.target.value)}
                        className="h-9 bg-white text-center"
                        data-testid={`measurement-${field.key}`}
                      />
                    </div>
                  ))}
                </div>

                <p className="text-xs text-slate-400 text-center mt-2">
                  💡 Ölçüleri değiştirdikten sonra "Ölçülere Göre Yenile" butonuna tıklayarak görseli güncelleyebilirsiniz.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Patient Info */}
        <Card className="border-0 shadow-lg bg-slate-50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[#5A67D8] flex items-center justify-center">
                  <Baby className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-medium text-slate-800">
                    {patient.first_name} {patient.last_name}
                  </p>
                  <p className="text-sm text-slate-500">
                    {patient.pregnancy_formatted || (patient.pregnancy_week ? `${patient.pregnancy_week}. hafta` : '')}
                    {patient.phone && ` • ${patient.phone}`}
                  </p>
                </div>
              </div>
              <div className="text-right text-sm text-slate-500">
                <p>Durum: <span className={`font-medium ${scan.status === 'generated' ? 'text-green-600' : 'text-orange-500'}`}>
                  {scan.status === 'generated' ? 'Tamamlandı' : 'Taslak'}
                </span></p>
                <p>Motor: {scan.engine || 'Fetal'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default StudioPage;
